import { useState, useEffect, useCallback } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { 
  ArrowLeft, Package, Clock, CheckCircle, Truck, ChefHat, 
  User, MapPin, Phone, DollarSign, CreditCard, MessageSquare,
  CheckCircle2, X, Building2, AlertCircle, Calendar
} from 'lucide-react'
import { api } from '../../../lib/api'
import { useAuth } from '../../../contexts/AuthContext'
import toast from 'react-hot-toast'
import ProductImage from '../../../components/ProductImage'
import { getCacheStale, setCache, removeCache } from '../../../lib/cache'
import { Order, Employee } from '../types'
import { useScrollHideHeader } from '../hooks/useScrollHideHeader'
import { useOrderDetail } from '../../../hooks/useWebSocket'

const DispatcherOrderDetails = () => {
  const navigate = useNavigate()
  const params = useParams()
  const { user } = useAuth()
  const [order, setOrder] = useState<Order | null>(null)
  const [chefs, setChefs] = useState<Employee[]>([])
  const [deliveryDrivers, setDeliveryDrivers] = useState<Employee[]>([])
  const [loading, setLoading] = useState(true)
  const [assigningOrder, setAssigningOrder] = useState(false)
  const [showAssignModal, setShowAssignModal] = useState(false)
  const [assignType, setAssignType] = useState<'chef' | 'delivery' | null>(null)
  const [updatingStatus, setUpdatingStatus] = useState(false)
  
  const isHeaderVisible = useScrollHideHeader()

  const orderId = params.orderId
  const storeId = params.storeId || user?.storeId

  useEffect(() => {
    if (orderId && storeId) {
      loadOrder()
      loadEmployees()
    }
  }, [orderId, storeId])

  // WebSocket: Recibir actualizaciones del pedido específico en tiempo real
  useOrderDetail(
    orderId,
    useCallback((updatedOrder: Order) => {
      // Normalizar el pedido actualizado
      const normalizedOrder: Order = {
        ...updatedOrder,
        items: (updatedOrder.items || []).map((item: any) => ({
          ...item,
          unit_price: Number(item.unit_price || item.unitPrice || 0),
          quantity: Number(item.quantity || 0)
        }))
      }

      // Actualizar el pedido en el estado
      setOrder(prevOrder => {
        if (!prevOrder || prevOrder.id !== normalizedOrder.id) {
          return normalizedOrder
        }

        // Verificar si el estado cambió para mostrar notificación
        const statusChanged = prevOrder.status !== normalizedOrder.status
        const assignedChanged = 
          prevOrder.assigned_chef_id !== normalizedOrder.assigned_chef_id ||
          prevOrder.assigned_delivery_id !== normalizedOrder.assigned_delivery_id

        // Actualizar cache
        const cacheKey = `dispatcher_order_${normalizedOrder.id}`
        setCache(cacheKey, normalizedOrder, 5 * 60 * 1000)

        // Mostrar notificación si cambió el estado o asignación
        if (statusChanged) {
          toast.success(`Estado actualizado: ${normalizedOrder.status}`)
        } else if (assignedChanged) {
          toast.success('Asignación actualizada')
        }

        return normalizedOrder
      })
    }, [])
  )

  const loadOrder = async () => {
    if (!orderId) return

    const cacheKey = `dispatcher_order_${orderId}`
    
    // Normalizar items del cache también
    const cachedOrder = getCacheStale<Order>(cacheKey)
    if (cachedOrder) {
      const normalizedCachedOrder: Order = {
        ...cachedOrder,
        items: (cachedOrder.items || []).map((item: any) => ({
          ...item,
          unit_price: Number(item.unit_price || item.unitPrice || 0),
          quantity: Number(item.quantity || 0)
        }))
      }
      setOrder(normalizedCachedOrder)
      setLoading(false)
    }

    try {
      const response = await api.get(`/orders/${orderId}`)
      if (response.data.success) {
        const orderData = response.data.data
        const normalizedOrder: Order = {
          id: orderData.id,
          store_id: orderData.store_id || orderData.storeId,
          customer_name: orderData.customer_name || orderData.customerName,
          customer_phone: orderData.customer_phone || orderData.customerPhone,
          customer_email: orderData.customer_email || orderData.customerEmail,
          status: orderData.status,
          total_amount: Number(orderData.total_amount || orderData.totalAmount) || 0,
          delivery_fee: Number(orderData.delivery_fee || orderData.deliveryFee) || 0,
          tax_amount: Number(orderData.tax_amount || orderData.taxAmount) || 0,
          estimated_delivery_time: Number(orderData.estimated_delivery_time || orderData.estimatedDeliveryTime) || 0,
          assigned_chef_id: orderData.assigned_chef_id || orderData.assignedChefId,
          assigned_delivery_id: orderData.assigned_delivery_id || orderData.assignedDeliveryId,
          assigned_chef: orderData.assigned_chef || orderData.assignedChef,
          assigned_delivery: orderData.assigned_delivery || orderData.assignedDelivery,
          created_at: orderData.created_at || orderData.createdAt,
          updated_at: orderData.updated_at || orderData.updatedAt,
          items: (orderData.items || []).map((item: any) => ({
            ...item,
            unit_price: Number(item.unit_price || item.unitPrice || 0),
            quantity: Number(item.quantity || 0)
          })),
          delivery_address: orderData.delivery_address || orderData.deliveryAddress,
          notes: orderData.notes,
          payment_method: orderData.payment_method || orderData.paymentMethod,
          cancellation_reason: orderData.cancellation_reason || orderData.cancellationReason,
          priority: orderData.priority || false
        }
        setOrder(normalizedOrder)
        setCache(cacheKey, normalizedOrder, 5 * 60 * 1000)
      }
    } catch (error: any) {
      console.error('Error loading order:', error)
      if (!cachedOrder) {
        toast.error('Error al cargar el pedido')
      }
    } finally {
      setLoading(false)
    }
  }

  const loadEmployees = async () => {
    if (!storeId) return

    // Usar cache compartido del dashboard
    const cacheKey = `dispatcher_employees_${storeId}`
    const cachedEmployees = getCacheStale<Employee[]>(cacheKey)
    
    if (cachedEmployees && cachedEmployees.length > 0) {
      setChefs(cachedEmployees.filter((e: Employee) => e.type === 'chef' && e.is_active))
      setDeliveryDrivers(cachedEmployees.filter((e: Employee) => e.type === 'delivery' && e.is_active))
      
      // Verificar edad del cache - solo hacer request si es muy antiguo (>5 min)
      const cacheAge = Date.now() - (localStorage.getItem(`colmafacil_cache_${cacheKey}`) ? JSON.parse(localStorage.getItem(`colmafacil_cache_${cacheKey}`) || '{}').timestamp || 0 : 0)
      if (cacheAge < 5 * 60 * 1000) {
        return // Cache es reciente, no hacer request
      }
    }

    try {
      const response = await api.get(`/auth/store/${storeId}/employees`)
      if (response.data.success) {
        const employees = response.data.data || []
        setChefs(employees.filter((e: Employee) => e.type === 'chef' && e.is_active))
        setDeliveryDrivers(employees.filter((e: Employee) => e.type === 'delivery' && e.is_active))
        // Actualizar cache compartido
        setCache(cacheKey, employees, 10 * 60 * 1000)
      }
    } catch (error: any) {
      console.error('Error loading employees:', error)
      // Si hay error pero tenemos cache, mantenerlo
      if (!cachedEmployees) {
        toast.error('Error al cargar empleados')
      }
    }
  }

  const handleAssign = (type: 'chef' | 'delivery') => {
    setAssignType(type)
    setShowAssignModal(true)
  }

  const confirmAssign = async (employeeId: string) => {
    if (!order || !assignType) return

    try {
      setAssigningOrder(true)
      const updateData: any = {
        status: assignType === 'chef' ? 'preparing' : order.status === 'ready' ? 'delivering' : order.status
      }

      if (assignType === 'chef') {
        updateData.assignedChefId = employeeId
      } else {
        updateData.assignedDeliveryId = employeeId
        if (order.status === 'ready') {
          updateData.status = 'delivering'
        }
      }

      const response = await api.patch(`/orders/${order.id}/status`, updateData)
      
      if (response.data.success) {
        toast.success(`${assignType === 'chef' ? 'Chef' : 'Delivery'} asignado exitosamente`)
        setShowAssignModal(false)
        setAssignType(null)
        // Invalidar cache del pedido y de la lista de pedidos
        if (order) {
          removeCache(`dispatcher_order_${order.id}`)
          if (storeId) {
            removeCache(`dispatcher_orders_${storeId}`)
          }
        }
        loadOrder()
      } else {
        toast.error(response.data.message || 'Error al asignar')
      }
    } catch (error: any) {
      console.error('Error assigning:', error)
      toast.error(error.response?.data?.message || 'Error al asignar empleado')
    } finally {
      setAssigningOrder(false)
    }
  }

  const handleStatusChange = async (newStatus: string) => {
    if (!order) return

    try {
      setUpdatingStatus(true)
      const response = await api.patch(`/orders/${order.id}/status`, { status: newStatus })
      
      if (response.data.success) {
        toast.success('Estado actualizado exitosamente')
        // Invalidar cache del pedido y de la lista de pedidos
        if (order) {
          removeCache(`dispatcher_order_${order.id}`)
          if (storeId) {
            removeCache(`dispatcher_orders_${storeId}`)
          }
        }
        await loadOrder()
      } else {
        toast.error(response.data.message || 'Error al actualizar estado')
      }
    } catch (error: any) {
      console.error('Error updating status:', error)
      toast.error(error.response?.data?.message || 'Error al actualizar estado')
    } finally {
      setUpdatingStatus(false)
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-amber-50 text-amber-900 border-amber-300'
      case 'preparing': return 'bg-blue-50 text-blue-900 border-blue-300'
      case 'ready': return 'bg-indigo-50 text-indigo-900 border-indigo-300'
      case 'ready_for_pickup': return 'bg-purple-50 text-purple-900 border-purple-300'
      case 'picked_up': return 'bg-cyan-50 text-cyan-900 border-cyan-300'
      case 'delivering': return 'bg-orange-50 text-orange-900 border-orange-300'
      case 'delivered': return 'bg-emerald-50 text-emerald-900 border-emerald-300'
      case 'cancelled': return 'bg-red-50 text-red-900 border-red-300'
      default: return 'bg-gray-50 text-gray-900 border-gray-300'
    }
  }

  const getStatusText = (status: string) => {
    switch (status) {
      case 'pending': return 'Pendiente'
      case 'preparing': return 'Preparando'
      case 'ready': return 'Listo'
      case 'ready_for_pickup': return 'Listo para Recoger'
      case 'picked_up': return 'Recogido'
      case 'delivering': return 'En Camino'
      case 'delivered': return 'Entregado'
      case 'cancelled': return 'Cancelado'
      default: return status
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 pt-16 flex items-center justify-center">
        <div className="text-center">
          <Package className="w-8 h-8 animate-pulse mx-auto mb-4 text-gray-600" />
          <p className="text-gray-700 font-light">Cargando pedido...</p>
        </div>
      </div>
    )
  }

  if (!order) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 pt-16 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-8 h-8 mx-auto mb-4 text-red-600" />
          <p className="text-gray-700 font-light">Pedido no encontrado</p>
          <button
            onClick={() => navigate('/dispatcher')}
            className="mt-4 px-4 py-2 bg-gradient-to-r from-gray-700 to-gray-800 text-white rounded-xl shadow-md shadow-gray-900/20 hover:shadow-lg hover:shadow-gray-900/30 transition-all duration-200 font-medium active:scale-95"
          >
            Volver al Dashboard
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 pt-16 pb-20">
      {/* Header - Fixed with scroll hide */}
      <div className={`fixed top-0 left-0 right-0 z-40 bg-white/80 backdrop-blur-xl border-b border-gray-200/50 shadow-lg shadow-black/5 transition-transform duration-300 ${
        isHeaderVisible ? 'translate-y-0' : '-translate-y-full'
      }`}>
        <div className="max-w-4xl mx-auto px-3 sm:px-4 py-3 sm:py-4">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/dispatcher')}
              className="p-2 hover:bg-gray-100/50 rounded-xl transition-colors active:scale-95"
            >
              <ArrowLeft className="w-5 h-5 text-gray-900" />
            </button>
            <div className="flex-1 min-w-0">
              <h1 className="text-xl sm:text-2xl font-light text-gray-900 truncate">Detalles del Pedido</h1>
              <p className="text-sm text-gray-600 truncate">#{order.id.substring(0, 8)}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-3 sm:px-4 py-4 sm:py-6">
        {/* Status Card */}
        <div className="bg-white/70 backdrop-blur-2xl rounded-2xl sm:rounded-3xl shadow-xl shadow-black/5 border border-white/60 p-3 sm:p-4 mb-3 sm:mb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 sm:gap-3">
              <span className={`px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-xl text-xs sm:text-sm font-medium border ${getStatusColor(order.status)}`}>
                {getStatusText(order.status)}
              </span>
              {order.priority && (
                <span className="px-2 py-1 bg-red-100 text-red-700 text-[10px] sm:text-xs font-medium rounded-xl border border-red-200">
                  ⚡ PRIORIDAD
                </span>
              )}
            </div>
            <div className="text-right">
              <p className="text-[10px] sm:text-xs font-medium text-gray-500 uppercase tracking-wide">Total</p>
              <p className="text-lg sm:text-xl font-light text-gray-900">RD${(order.total_amount + order.delivery_fee).toFixed(2)}</p>
            </div>
          </div>
        </div>

        {/* Customer Info */}
        <div className="bg-white/70 backdrop-blur-2xl rounded-2xl sm:rounded-3xl shadow-xl shadow-black/5 border border-white/60 p-3 sm:p-4 md:p-5 mb-3 sm:mb-4">
          <h2 className="text-base sm:text-lg font-light text-gray-900 mb-2 sm:mb-3 flex items-center gap-2">
            <User className="w-4 h-4 sm:w-5 sm:h-5" />
            Información del Cliente
          </h2>
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-gray-700">Nombre:</span>
              <span className="text-sm text-gray-900">{order.customer_name}</span>
            </div>
            <div className="flex items-center gap-2">
              <Phone className="w-4 h-4 text-gray-500" />
              <span className="text-sm text-gray-900">{order.customer_phone}</span>
            </div>
            {order.customer_email && (
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-gray-700">Email:</span>
                <span className="text-sm text-gray-900">{order.customer_email}</span>
              </div>
            )}
          </div>
        </div>

        {/* Order Items */}
        <div className="bg-white/70 backdrop-blur-2xl rounded-2xl sm:rounded-3xl shadow-xl shadow-black/5 border border-white/60 p-3 sm:p-4 md:p-5 mb-3 sm:mb-4">
          <h2 className="text-base sm:text-lg font-light text-gray-900 mb-2 sm:mb-3 flex items-center gap-2">
            <Package className="w-4 h-4 sm:w-5 sm:h-5" />
            Productos ({order.items.length})
          </h2>
          <div className="space-y-2 sm:space-y-3">
            {order.items.map((item) => (
              <div key={item.id} className="flex items-center gap-2 sm:gap-3 p-2.5 sm:p-3 bg-white/50 backdrop-blur-sm rounded-xl sm:rounded-2xl border border-gray-200/50">
                {item.product.image_url && (
                  <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-xl sm:rounded-2xl overflow-hidden border-2 border-gray-200/50 flex-shrink-0">
                    <ProductImage 
                      imageKey={item.product.image_url} 
                      alt={item.product.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-xs sm:text-sm text-gray-900 truncate">{item.product.name}</p>
                  {item.variant && (
                    <p className="text-[10px] sm:text-xs text-gray-600">Variante: {item.variant.variant_name}</p>
                  )}
                  <p className="text-[10px] sm:text-xs text-gray-500">Cantidad: {item.quantity}</p>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="font-semibold text-xs sm:text-sm text-gray-900">RD${(Number(item.unit_price || 0) * Number(item.quantity || 0)).toFixed(2)}</p>
                  <p className="text-[10px] sm:text-xs text-gray-500">RD${Number(item.unit_price || 0).toFixed(2)} c/u</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Delivery Address */}
        {order.delivery_address && (
          <div className="bg-white/70 backdrop-blur-2xl rounded-2xl sm:rounded-3xl shadow-xl shadow-black/5 border border-white/60 p-3 sm:p-4 md:p-5 mb-3 sm:mb-4">
            <h2 className="text-base sm:text-lg font-light text-gray-900 mb-2 sm:mb-3 flex items-center gap-2">
              <MapPin className="w-4 h-4 sm:w-5 sm:h-5" />
              Dirección de Entrega
            </h2>
            <p className="text-xs sm:text-sm text-gray-900">{order.delivery_address}</p>
          </div>
        )}

        {/* Payment Info */}
        <div className="bg-white/70 backdrop-blur-2xl rounded-2xl sm:rounded-3xl shadow-xl shadow-black/5 border border-white/60 p-3 sm:p-4 md:p-5 mb-3 sm:mb-4">
          <h2 className="text-base sm:text-lg font-light text-gray-900 mb-2 sm:mb-3 flex items-center gap-2">
            <CreditCard className="w-4 h-4 sm:w-5 sm:h-5" />
            Información de Pago
          </h2>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-xs sm:text-sm text-gray-600">Subtotal:</span>
              <span className="text-xs sm:text-sm font-medium text-gray-900">RD${order.total_amount.toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-xs sm:text-sm text-gray-600">Delivery:</span>
              <span className="text-xs sm:text-sm font-medium text-gray-900">RD${order.delivery_fee.toFixed(2)}</span>
            </div>
            {order.tax_amount && order.tax_amount > 0 && (
              <div className="flex justify-between">
                <span className="text-xs sm:text-sm text-gray-600">ITBIS:</span>
                <span className="text-xs sm:text-sm font-medium text-gray-900">RD${order.tax_amount.toFixed(2)}</span>
              </div>
            )}
            <div className="border-t border-gray-200/50 pt-2 mt-2">
              <div className="flex justify-between">
                <span className="text-sm sm:text-base font-light text-gray-900">Total:</span>
                <span className="text-sm sm:text-base font-light text-gray-900">RD${(order.total_amount + order.delivery_fee + (order.tax_amount || 0)).toFixed(2)}</span>
              </div>
            </div>
            {order.payment_method && (
              <div className="mt-2 pt-2 border-t border-gray-200/50">
                <span className="text-[10px] sm:text-xs font-medium text-gray-500 uppercase tracking-wide">Método de Pago:</span>
                <span className="ml-2 text-xs sm:text-sm text-gray-900 capitalize">{order.payment_method}</span>
              </div>
            )}
          </div>
        </div>

        {/* Assigned Staff */}
        <div className="bg-white/70 backdrop-blur-2xl rounded-2xl sm:rounded-3xl shadow-xl shadow-black/5 border border-white/60 p-3 sm:p-4 md:p-5 mb-3 sm:mb-4">
          <h2 className="text-base sm:text-lg font-light text-gray-900 mb-2 sm:mb-3">Personal Asignado</h2>
          <div className="space-y-2">
            {order.assigned_chef ? (
              <div className="flex items-center justify-between p-2.5 sm:p-3 bg-blue-50/80 backdrop-blur-sm rounded-xl sm:rounded-2xl border border-blue-200/50">
                <div className="flex items-center gap-2">
                  <ChefHat className="w-4 h-4 text-blue-600" />
                  <span className="text-xs sm:text-sm font-medium text-blue-900">Chef: {order.assigned_chef.name}</span>
                </div>
              </div>
            ) : (
              order.status === 'pending' && (
                <button
                  onClick={() => handleAssign('chef')}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl sm:rounded-2xl shadow-md shadow-blue-500/20 hover:shadow-lg hover:shadow-blue-500/30 hover:from-blue-600 hover:to-blue-700 transition-all duration-200 font-medium text-xs sm:text-sm active:scale-95"
                >
                  <ChefHat className="w-4 h-4" />
                  Asignar Chef
                </button>
              )
            )}
            {order.assigned_delivery ? (
              <div className="flex items-center justify-between p-2.5 sm:p-3 bg-orange-50/80 backdrop-blur-sm rounded-xl sm:rounded-2xl border border-orange-200/50">
                <div className="flex items-center gap-2">
                  <Truck className="w-4 h-4 text-orange-600" />
                  <span className="text-xs sm:text-sm font-medium text-orange-900">Delivery: {order.assigned_delivery.name}</span>
                </div>
              </div>
            ) : (
              (order.status === 'ready' || order.status === 'ready_for_pickup') && (
                <button
                  onClick={() => handleAssign('delivery')}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-xl sm:rounded-2xl shadow-md shadow-orange-500/20 hover:shadow-lg hover:shadow-orange-500/30 hover:from-orange-600 hover:to-orange-700 transition-all duration-200 font-medium text-xs sm:text-sm active:scale-95"
                >
                  <Truck className="w-4 h-4" />
                  Asignar Delivery
                </button>
              )
            )}
          </div>
        </div>

        {/* Notes */}
        {order.notes && (
          <div className="bg-white/70 backdrop-blur-2xl rounded-2xl sm:rounded-3xl shadow-xl shadow-black/5 border border-white/60 p-3 sm:p-4 md:p-5 mb-3 sm:mb-4">
            <h2 className="text-base sm:text-lg font-light text-gray-900 mb-2 sm:mb-3 flex items-center gap-2">
              <MessageSquare className="w-4 h-4 sm:w-5 sm:h-5" />
              Notas
            </h2>
            <p className="text-xs sm:text-sm text-gray-900">{order.notes}</p>
          </div>
        )}

        {/* Timestamps */}
        <div className="bg-white/70 backdrop-blur-2xl rounded-2xl sm:rounded-3xl shadow-xl shadow-black/5 border border-white/60 p-3 sm:p-4 md:p-5 mb-3 sm:mb-4">
          <h2 className="text-base sm:text-lg font-light text-gray-900 mb-2 sm:mb-3 flex items-center gap-2">
            <Calendar className="w-4 h-4 sm:w-5 sm:h-5" />
            Información Temporal
          </h2>
          <div className="space-y-2 text-xs sm:text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Creado:</span>
              <span className="text-gray-900 font-medium">{new Date(order.created_at).toLocaleString('es-DO')}</span>
            </div>
            {order.updated_at && (
              <div className="flex justify-between">
                <span className="text-gray-600">Actualizado:</span>
                <span className="text-gray-900 font-medium">{new Date(order.updated_at).toLocaleString('es-DO')}</span>
              </div>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="bg-white/70 backdrop-blur-2xl rounded-2xl sm:rounded-3xl shadow-xl shadow-black/5 border border-white/60 p-3 sm:p-4 md:p-5">
          <h2 className="text-base sm:text-lg font-light text-gray-900 mb-2 sm:mb-3">Acciones</h2>
          <div className="flex flex-wrap gap-2">
            {order.status === 'preparing' && (
              <button
                onClick={() => handleStatusChange('ready')}
                disabled={updatingStatus}
                className="flex items-center justify-center gap-2 px-3 sm:px-4 py-2 bg-gradient-to-r from-indigo-500 to-indigo-600 text-white rounded-xl sm:rounded-2xl shadow-md shadow-indigo-500/20 hover:shadow-lg hover:shadow-indigo-500/30 hover:from-indigo-600 hover:to-indigo-700 transition-all duration-200 disabled:opacity-50 font-medium text-xs sm:text-sm active:scale-95"
              >
                <CheckCircle2 className="w-4 h-4" />
                <span className="hidden sm:inline">Marcar como Listo</span>
                <span className="sm:hidden">Listo</span>
              </button>
            )}
            {order.status === 'ready' && order.assigned_delivery_id && (
              <button
                onClick={() => handleStatusChange('delivering')}
                disabled={updatingStatus}
                className="flex items-center justify-center gap-2 px-3 sm:px-4 py-2 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-xl sm:rounded-2xl shadow-md shadow-orange-500/20 hover:shadow-lg hover:shadow-orange-500/30 hover:from-orange-600 hover:to-orange-700 transition-all duration-200 disabled:opacity-50 font-medium text-xs sm:text-sm active:scale-95"
              >
                <Truck className="w-4 h-4" />
                <span className="hidden sm:inline">Iniciar Entrega</span>
                <span className="sm:hidden">Iniciar</span>
              </button>
            )}
            {order.status === 'delivering' && (
              <button
                onClick={() => handleStatusChange('delivered')}
                disabled={updatingStatus}
                className="flex items-center justify-center gap-2 px-3 sm:px-4 py-2 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white rounded-xl sm:rounded-2xl shadow-md shadow-emerald-500/20 hover:shadow-lg hover:shadow-emerald-500/30 hover:from-emerald-600 hover:to-emerald-700 transition-all duration-200 disabled:opacity-50 font-medium text-xs sm:text-sm active:scale-95"
              >
                <CheckCircle2 className="w-4 h-4" />
                <span className="hidden sm:inline">Marcar como Entregado</span>
                <span className="sm:hidden">Entregado</span>
              </button>
            )}
            {order.status !== 'delivered' && order.status !== 'cancelled' && (
              <button
                onClick={() => handleStatusChange('cancelled')}
                disabled={updatingStatus}
                className="flex items-center justify-center gap-2 px-3 sm:px-4 py-2 bg-white/80 backdrop-blur-sm text-red-600 rounded-xl sm:rounded-2xl border-2 border-red-200/50 hover:bg-red-50/80 hover:border-red-300/50 shadow-md transition-all duration-200 disabled:opacity-50 font-medium text-xs sm:text-sm active:scale-95"
              >
                <X className="w-4 h-4" />
                <span className="hidden sm:inline">Cancelar Pedido</span>
                <span className="sm:hidden">Cancelar</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Assign Modal */}
      {showAssignModal && assignType && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-3 sm:p-4">
          <div className="bg-white/90 backdrop-blur-2xl rounded-2xl sm:rounded-3xl shadow-2xl shadow-black/20 max-w-md w-full max-h-[90vh] overflow-y-auto border border-white/60">
            <div className="p-4 sm:p-6 md:p-8">
              <div className="flex items-center justify-between mb-4 sm:mb-6">
                <h3 className="text-xl sm:text-2xl font-light text-gray-900">
                  Asignar {assignType === 'chef' ? 'Chef' : 'Delivery'}
                </h3>
                <button
                  onClick={() => {
                    setShowAssignModal(false)
                    setAssignType(null)
                  }}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-2 sm:space-y-3 max-h-[50vh] sm:max-h-64 overflow-y-auto">
                {(assignType === 'chef' ? chefs : deliveryDrivers).map(employee => (
                  <button
                    key={employee.id}
                    onClick={() => confirmAssign(employee.id)}
                    disabled={assigningOrder}
                    className="w-full p-3 sm:p-4 text-left bg-white/50 backdrop-blur-sm hover:bg-white/70 rounded-xl sm:rounded-2xl border border-gray-200/50 transition-all duration-200 disabled:opacity-50 hover:shadow-md"
                  >
                    <p className="font-medium text-gray-900 text-sm sm:text-base truncate">{employee.name}</p>
                    <p className="text-xs sm:text-sm text-gray-500 truncate">{employee.email}</p>
                  </button>
                ))}
              </div>

              {((assignType === 'chef' ? chefs : deliveryDrivers).length === 0) && (
                <p className="text-center text-gray-500 py-4 text-sm">
                  No hay {assignType === 'chef' ? 'chefs' : 'delivery'} disponibles
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default DispatcherOrderDetails

